/**
 * Six-cell PIN input for 6-digit TOTP codes (and similar OTP-style entry).
 *
 * Behaviour:
 *   - Each cell accepts a single digit; typing advances focus to the next cell.
 *   - Backspace on an empty cell jumps focus back and clears the previous cell.
 *   - Left/Right arrows move between cells.
 *   - Pasting a string of 6 digits distributes them across all cells in one go
 *     (works on any cell, not just the first).
 *   - Non-digit characters are filtered out at input time.
 *   - The component is fully controlled — parent owns the value (a string of
 *     0–6 digits). Renders a length-aware visual but does NOT enforce length;
 *     the consuming form's schema is the source of truth for "complete or not".
 *
 * Using six cells (instead of one masked text field) deliberately:
 *   - Visual feedback per digit makes typos obvious instantly.
 *   - Native browser validation can't drift out of sync with the regex.
 *   - Paste-from-clipboard is the natural shape of how authenticator apps
 *     present codes.
 */
import { useEffect, useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

export interface PinCodeInputProps {
  /** Current value (0–6 digits). Anything beyond 6 chars is truncated on render. */
  value: string;
  /** Called whenever the value changes. Always 0–6 digits. */
  onChange: (next: string) => void;
  /** Triggered when the 6th digit is entered — convenience for auto-submit. */
  onComplete?: (value: string) => void;
  /** Number of cells (default 6 — supports 8 for backup codes if needed). */
  length?: number;
  /** Visual error state (red border + ring). */
  isInvalid?: boolean;
  /** Disables all cells. */
  isDisabled?: boolean;
  /** Auto-focus first cell on mount. */
  autoFocus?: boolean;
  /** Optional label text rendered above the cells. */
  label?: string;
  /** Error message shown below the cells when isInvalid. */
  errorMessage?: string;
  /** aria-label fallback when no visible label is provided. */
  ariaLabel?: string;
}

export function PinCodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  isInvalid = false,
  isDisabled = false,
  autoFocus = false,
  label,
  errorMessage,
  ariaLabel,
}: PinCodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const labelId = useId();

  // Truncate to `length` so out-of-band parent values don't break rendering.
  const safeValue = value.slice(0, length);
  const cells = Array.from({ length }, (_, i) => safeValue[i] ?? '');

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  // Notify on completion. We watch `safeValue.length` rather than firing inside
  // setSingleCell so a paste that fills all cells in one go also triggers it.
  const lastReportedRef = useRef<string | null>(null);
  useEffect(() => {
    if (safeValue.length === length && lastReportedRef.current !== safeValue) {
      lastReportedRef.current = safeValue;
      onComplete?.(safeValue);
    } else if (safeValue.length !== length) {
      lastReportedRef.current = null;
    }
  }, [safeValue, length, onComplete]);

  function focusCell(index: number) {
    const input = inputsRef.current[Math.max(0, Math.min(length - 1, index))];
    input?.focus();
    input?.select();
  }

  function setSingleCell(index: number, digit: string) {
    // `digit` is already filtered to a single 0–9 character (or '' for clear).
    const chars = cells.slice();
    chars[index] = digit;
    // Compact: trailing empty cells stay empty, but we don't allow gaps in
    // the middle — value is always a left-aligned prefix.
    const next = chars.join('').slice(0, length);
    onChange(next);
  }

  function handleChange(index: number, raw: string) {
    // Paste handler covers multi-digit input; this path only sees single chars.
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) {
      // Pure clear (e.g. user selected the cell and pressed Delete).
      setSingleCell(index, '');
      return;
    }
    setSingleCell(index, digit);
    if (index < length - 1) focusCell(index + 1);
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      // If current cell has a value, clear it in-place. Otherwise, hop back
      // and clear the previous cell — matches how every native OTP UI does it.
      if (cells[index]) {
        setSingleCell(index, '');
        return;
      }
      if (index > 0) {
        e.preventDefault();
        setSingleCell(index - 1, '');
        focusCell(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusCell(index - 1);
      return;
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusCell(index + 1);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      focusCell(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      focusCell(length - 1);
      return;
    }
  }

  function handlePaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    // Fill from the cell that received the paste — most users paste into the
    // first cell, but starting elsewhere is honoured rather than swallowed.
    const before = cells.slice(0, index).join('');
    const merged = (before + pasted).slice(0, length);
    onChange(merged);
    const finalIndex = Math.min(merged.length, length - 1);
    // Focus shifts to the cell after the last filled one (or the last cell).
    requestAnimationFrame(() => focusCell(finalIndex));
  }

  return (
    <div>
      {label && (
        <label
          id={labelId}
          style={{
            display: 'block',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-secondary)',
            marginBottom: 6,
            fontFamily: 'var(--font-sans)',
          }}
        >
          {label}
        </label>
      )}
      <div
        role="group"
        aria-labelledby={label ? labelId : undefined}
        aria-label={!label ? (ariaLabel ?? 'Verification code') : undefined}
        style={{ display: 'flex', gap: 8 }}
      >
        {cells.map((digit, i) => {
          const borderColor = isInvalid
            ? 'var(--color-error)'
            : digit
              ? 'var(--color-accent)'
              : 'var(--color-surface-border)';
          const ringColor = isInvalid ? 'var(--color-error-subtle)' : 'var(--color-accent-subtle)';
          return (
            <input
              key={i}
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              onFocus={(e) => e.target.select()}
              disabled={isDisabled}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              aria-label={`Digit ${i + 1} of ${length}`}
              aria-invalid={isInvalid || undefined}
              style={{
                flex: 1,
                minWidth: 0,
                height: 'var(--control-height-lg, 48px)',
                textAlign: 'center',
                fontSize: 'var(--text-lg, 18px)',
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: 'var(--font-sans)',
                color: 'var(--color-text)',
                background: 'var(--color-surface-solid)',
                border: `1px solid ${borderColor}`,
                borderRadius: 'var(--control-radius-md, 10px)',
                outline: 'none',
                boxShadow: digit
                  ? `0 0 0 3px ${ringColor}, inset 0 0.5px 0 var(--color-surface-highlight)`
                  : 'var(--control-shadow-inset)',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : undefined,
              }}
            />
          );
        })}
      </div>
      {errorMessage && isInvalid && (
        <span
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 'var(--weight-medium)',
            color: 'var(--color-error-text)',
            marginTop: 5,
            fontFamily: 'var(--font-sans)',
          }}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
}
